import { useEffect, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthContext } from '../../contexts/AuthContext'
import { APP_WORKSPACE_PATH, GUEST_APP_ENTRY_PATH } from '../../lib/appRoutes'
import { canOpenAppFromMarketingLogo } from '../../lib/marketingQuickEntry'

const NAV_LINKS = [
  { label: 'Features', href: '/features' },
  { label: 'How it works', href: '/how-it-works' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'FAQ', href: '/#faq' },
]

const font = "'Geist', 'Geist Variable', ui-sans-serif, system-ui, sans-serif"

export default function MarketingNav() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const auth = useAuthContext()
  const quickAppFromLogo =
    typeof window !== 'undefined' && !auth.user && canOpenAppFromMarketingLogo()
  const logoHome = auth.user ? APP_WORKSPACE_PATH : quickAppFromLogo ? GUEST_APP_ENTRY_PATH : '/'

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 80px',
        background: scrolled ? 'color-mix(in srgb, var(--lp-bg) 95%, transparent)' : 'var(--lp-bg)',
        backdropFilter: scrolled ? 'blur(8px)' : 'none',
        borderBottom: '1px solid var(--lp-border)',
        transition: 'background 200ms ease',
      }}
    >
      {/* Logo */}
      <Link
        to={logoHome}
        onClick={() => {
          if (logoHome === '/') window.scrollTo({ top: 0, behavior: 'smooth' })
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          textDecoration: 'none',
          flexShrink: 0,
        }}
      >
        <img src="/CornerLogo.svg" alt="Corner" style={{ width: 22, height: 22 }} />
        <span
          style={{
            fontSize: '16px',
            fontWeight: 600,
            color: 'var(--lp-text)',
            fontFamily: font,
            letterSpacing: '-0.02em',
            lineHeight: '20px',
          }}
        >
          Corner
        </span>
      </Link>

      {/* Desktop center links */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 32,
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
        className="hidden-mobile"
      >
        {NAV_LINKS.map((link) => {
          const isHash = link.href.startsWith('/#')
          const isActive = !isHash && location.pathname === link.href
          const linkStyle = {
            fontSize: '14px',
            fontWeight: isActive ? 600 : 400,
            color: isActive ? '#D46A3A' : 'var(--lp-text-muted)',
            fontFamily: font,
            textDecoration: 'none',
            transition: 'color 150ms ease, opacity 150ms ease',
            cursor: 'pointer',
          } as const
          if (isHash) {
            const id = link.href.slice(2)
            return (
              <a
                key={link.href}
                href={link.href}
                style={linkStyle}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.opacity = '0.7' }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.opacity = '1' }}
                onClick={(e) => {
                  e.preventDefault()
                  if (location.pathname === '/') {
                    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
                  } else {
                    navigate({ pathname: '/', hash: `#${id}` })
                  }
                }}
              >
                {link.label}
              </a>
            )
          }
          return (
            <Link
              key={link.href}
              to={link.href}
              style={linkStyle}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.opacity = '0.7' }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.opacity = '1' }}
            >
              {link.label}
            </Link>
          )
        })}
      </div>

      {/* Desktop right actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }} className="hidden-mobile">
        <Link
          to="/auth?mode=login"
          style={{
            fontSize: '14px',
            fontWeight: 400,
            color: 'var(--lp-text-muted)',
            fontFamily: font,
            textDecoration: 'none',
            transition: 'opacity 150ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7' }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1' }}
        >
          Sign in
        </Link>
        <button
          type="button"
          onClick={() => navigate('/auth')}
          style={{
            padding: '8px 18px',
            background: '#D46A3A',
            color: '#fff',
            border: 'none',
            borderRadius: 20,
            fontSize: '13px',
            fontWeight: 600,
            fontFamily: font,
            cursor: 'pointer',
            transition: 'opacity 150ms ease',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85' }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1' }}
        >
          Get started →
        </button>
      </div>

      {/* Mobile hamburger */}
      <button
        type="button"
        onClick={() => setMenuOpen(!menuOpen)}
        style={{
          display: 'none',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 8,
          color: 'var(--lp-text)',
        }}
        className="show-mobile"
        aria-label="Toggle menu"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          {menuOpen ? (
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          ) : (
            <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          )}
        </svg>
      </button>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          style={{
            position: 'fixed',
            top: 60,
            left: 0,
            right: 0,
            background: 'var(--lp-bg)',
            borderBottom: '1px solid var(--lp-border)',
            padding: '16px 32px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {NAV_LINKS.map((link) => {
            const mobileStyle = {
              fontSize: '16px',
              fontWeight: 500,
              color: 'var(--lp-text)',
              fontFamily: font,
              textDecoration: 'none',
              padding: '10px 0',
              borderBottom: '1px solid var(--lp-border)',
              cursor: 'pointer',
            } as const
            if (link.href.startsWith('/#')) {
              const id = link.href.slice(2)
              return (
                <a
                  key={link.href}
                  href={link.href}
                  style={mobileStyle}
                  onClick={(e) => {
                    e.preventDefault()
                    setMenuOpen(false)
                    if (location.pathname === '/') {
                      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
                    } else {
                      navigate({ pathname: '/', hash: `#${id}` })
                    }
                  }}
                >
                  {link.label}
                </a>
              )
            }
            return (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMenuOpen(false)}
                style={mobileStyle}
              >
                {link.label}
              </Link>
            )
          })}
          <Link
            to="/auth?mode=login"
            onClick={() => setMenuOpen(false)}
            style={{
              fontSize: '16px',
              fontWeight: 500,
              color: 'var(--lp-text)',
              fontFamily: font,
              textDecoration: 'none',
              padding: '10px 0',
              borderBottom: '1px solid var(--lp-border)',
            }}
          >
            Sign in
          </Link>
          <button
            type="button"
            onClick={() => { setMenuOpen(false); navigate('/auth') }}
            style={{
              marginTop: 12,
              padding: '12px',
              background: '#D46A3A',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: '16px',
              fontWeight: 500,
              fontFamily: font,
              cursor: 'pointer',
              minHeight: 44,
            }}
          >
            Get started →
          </button>
        </div>
      )}
    </nav>
  )
}
